import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { getTailoringHistory, downloadTailoredResume } from '../services/tailor';
import Loader from '../components/common/Loader';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaMagic, 
  FaSearch, 
  FaChartBar, 
  FaEdit, 
  FaBullseye, 
  FaFileAlt, 
  FaCheckCircle, 
  FaExclamationCircle, 
  FaDownload,
  FaClock,
  FaStar,
  FaRegStar,
  FaEye
} from 'react-icons/fa';
import './Dashboard.css';
import './EnhancedDashboard.css';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { profileData, profileComplete } = useProfile();
  const [tailoringHistory, setTailoringHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [favoriteResumes, setFavoriteResumes] = useState([]);
  const [expandedResumeId, setExpandedResumeId] = useState(null);

  useEffect(() => {
    document.title += ' - Dashboard';
    // This will clean up the title when component unmounts
    return () => {
      document.title = 'CVance'; // Default title
    };
  }, []);

  // Fetch tailoring history on component mount
  useEffect(() => {
    const fetchTailoringHistory = async () => {
      try {
        const data = await getTailoringHistory();
        setTailoringHistory(data);
        
        // Load favorites from localStorage
        const storedFavorites = localStorage.getItem('favoriteResumes');
        if (storedFavorites) {
          setFavoriteResumes(JSON.parse(storedFavorites));
        }
      } catch (err) {
        setError('Failed to load resume history. Please try again later.');
        console.error('Error fetching history:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTailoringHistory();
  }, []);

  const handleDownloadResume = async (resumeId, event) => {
    event.preventDefault(); // Prevent default anchor behavior
    
    try {
      setIsDownloading(true);
      setDownloadingId(resumeId);
      
      // Call your API to download the resume
      await downloadTailoredResume(resumeId, "docx");
      
    } catch (error) {
      console.error('Error downloading resume:', error);
      // You could add error handling UI here
    } finally {
      setIsDownloading(false);
      setDownloadingId(null);
    }
  };
  
  const toggleExpandResume = (resumeId) => {
    setExpandedResumeId(expandedResumeId === resumeId ? null : resumeId);
  };
  
  const toggleFavorite = (resumeId) => {
    let newFavorites;
    
    if (favoriteResumes.includes(resumeId)) {
      newFavorites = favoriteResumes.filter(id => id !== resumeId);
    } else {
      newFavorites = [...favoriteResumes, resumeId];
    }
    
    setFavoriteResumes(newFavorites);
    localStorage.setItem('favoriteResumes', JSON.stringify(newFavorites));
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Calculate how long ago a date was
  const timeAgo = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    }
    
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
  };
  
  // Calculate profile completion percentage
  const calculateProfileCompletion = () => {
    const sections = [
      { name: 'Basic Info', completed: Boolean(profileData?.user?.first_name && profileData?.user?.last_name && profileData?.user?.email) },
      { name: 'Summary', completed: Boolean(profileData?.summary) },
      { name: 'Education', completed: profileData?.education?.length > 0 },
      { name: 'Skills', completed: profileData?.skill_categories?.length > 0 },
      { name: 'Experience', completed: profileData?.experiences?.length > 0 },
      { name: 'Projects', completed: profileData?.projects?.length > 0 }
    ];
    
    const completedSections = sections.filter(section => section.completed).length;
    const percentage = Math.round((completedSections / sections.length) * 100);
    
    return {
      percentage,
      sections
    };
  };
  
  const profileCompletionData = calculateProfileCompletion();
  
  if (loading || !currentUser) {
    return <Loader text="Loading dashboard..." />;
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 70,
        damping: 12
      }
    }
  };
  
  return (
    <motion.div 
      className="dashboard-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        <motion.div 
          className="dashboard-header"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="welcome-container">
            <h1>Welcome, <span className="highlight-name">{currentUser?.first_name || 'User'}</span>!</h1>
            <p className="welcome-subtitle">Let's make your resume stand out from the crowd</p>
            <div className="welcome-decoration"></div>
          </div>
          <div className="dashboard-actions">
            <Link to="/tailor" className="btn btn-primary pulse-animation">
              <FaMagic className="icon-left" /> Tailor a New Resume
            </Link>
          </div>
        </motion.div>
        
        {/* Profile completion section */}
        <motion.div 
          className="dashboard-section"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div 
            className="profile-completion-card"
            variants={itemVariants}
          >
            <div className="completion-header">
              <div className="completion-info">
                <h3>Your profile is {profileCompletionData.percentage}% complete</h3>
                <p>Complete your profile to get better results when tailoring your resume.</p>
              </div>
              <div className="completion-percentage">
                <div className="percentage-circle">
                  <svg viewBox="0 0 36 36" className="circular-chart">
                    <path
                      className="circle-bg"
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <motion.path
                      className="circle"
                      strokeDasharray={`${profileCompletionData.percentage}, 100`}
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      initial={{ strokeDasharray: "0, 100" }}
                      animate={{ strokeDasharray: `${profileCompletionData.percentage}, 100` }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                    />
                    <text x="18" y="20.35" className="percentage">
                      {profileCompletionData.percentage}%
                    </text>
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="completion-sections">
              {profileCompletionData.sections.map((section, index) => (
                <motion.div 
                  key={index} 
                  className={`completion-section ${section.completed ? 'completed' : 'incomplete'}`}
                  variants={itemVariants}
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <div className="section-status">
                    {section.completed ? (
                      <div className="status-icon-complete">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    ) : (
                      <div className="status-icon-incomplete">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 8v4" strokeLinecap="round" />
                          <circle cx="12" cy="16" r="1" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="section-name">
                    {section.name}
                  </div>
                  <div className="section-action">
                    {!section.completed && (
                      <Link to="/profile" className="btn btn-text">
                        Add
                      </Link>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
        
        {/* Resume history section */}
        <motion.div 
          className="dashboard-section"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div 
            className="section-header resume-section-header"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2>Recent Resumes</h2>
            <Link to="/history" className="view-all-link">
              <span>View All</span>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
                <path d="M5 12h14"></path>
                <path d="M12 5l7 7-7 7"></path>
              </svg>
            </Link>
          </motion.div>
          
          {error && (
            <motion.div 
              className="error-message"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.div>
          )}
          
          {!error && (!tailoringHistory || tailoringHistory.length === 0) ? (
            <motion.div 
              className="empty-state"
              variants={itemVariants}
            >
              <motion.div 
                className="empty-icon"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5, type: "spring" }}
              >
                <FaFileAlt />
              </motion.div>
              <motion.h3
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                No tailored resumes yet
              </motion.h3>
              <motion.p
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                Start tailoring your resume to specific job descriptions to see them here.
              </motion.p>
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="btn-container"
              >
                <Link to="/tailor" className="btn btn-primary pulse-effect">
                  <FaMagic style={{ marginRight: '8px' }} /> Tailor a Resume
                </Link>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div 
              className="resume-history"
              variants={containerVariants}
            >
              <AnimatePresence>
                {tailoringHistory && tailoringHistory.history && tailoringHistory.history.slice(0, 3).map((resume, index) => {
                  const resumeId = resume.session_id;
                  const isExpanded = expandedResumeId === resumeId;
                  const isFavorite = favoriteResumes.includes(resumeId);
                  
                  return (
                    <motion.div 
                      key={resumeId || index} 
                      className="resume-card"
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit={{ opacity: 0, y: 20, transition: { duration: 0.2 } }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <motion.div 
                        className="resume-icon"
                        whileHover={{ rotate: 5 }}
                      >
                        <FaFileAlt />
                      </motion.div>
                      <div className="resume-details">
                        <div className="resume-title-row">
                          <h3 className="resume-title">
                            {(resume?.job_analysis?.position_title || resume?.jobTitle) || 'Untitled Resume'}
                          </h3>
                          <button 
                            className={`favorite-button ${isFavorite ? 'favorited' : ''}`}
                            onClick={() => toggleFavorite(resumeId)}
                            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                          >
                            {isFavorite ? <FaStar /> : <FaRegStar />}
                          </button>
                        </div>
                        
                        {(resume?.job_analysis?.company_name || resume?.company) && (
                          <p className="resume-company">
                            {resume?.job_analysis?.company_name || resume?.company}
                          </p>
                        )}
                        
                        <p className="resume-date">
                          <FaClock style={{ marginRight: '8px', fontSize: '12px', opacity: 0.7 }} />
                          {timeAgo(resume.created_at || resume.createdAt)}
                        </p>
                        
                        {resume.match_score && (
                          <motion.div 
                            className="match-score"
                            whileHover={{ scale: 1.05 }}
                          >
                            <span className="score-label">Match Score:</span>
                            <span className="score-value">{Math.round((resume.match_score) * 100)}%</span>
                          </motion.div>
                        )}
                      </div>
                      <motion.div 
                        className="resume-actions"
                        whileHover={{ scale: 1.1 }}
                      >
                        <button 
                          className="btn btn-text btn-sm view-button"
                          onClick={() => toggleExpandResume(resumeId)}
                        >
                          <FaEye />
                        </button>
                        <button 
                          className="btn btn-text btn-sm download-button"
                          onClick={(e) => handleDownloadResume(resumeId, e)}
                          disabled={isDownloading && downloadingId === resumeId}
                        >
                          {isDownloading && downloadingId === resumeId ? (
                            <span className="loading-spinner"></span>
                          ) : (
                            <FaDownload />
                          )}
                        </button>
                      </motion.div>
                      
                      {isExpanded && (
                        <motion.div 
                          className="resume-expand-content"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="resume-section">
                            <h4>Created On</h4>
                            <p>{formatDate(resume.created_at || resume.createdAt)}</p>
                          </div>
                          
                          {resume?.job_analysis?.key_skills && resume.job_analysis.key_skills.length > 0 && (
                            <div className="resume-section">
                              <h4>Key Skills</h4>
                              <div className="skills-list">
                                {resume.job_analysis.key_skills.slice(0, 3).map((skill, i) => (
                                  <span key={i} className="skill-tag">{skill?.name}</span>
                                ))}
                                {resume.job_analysis.key_skills.length > 3 && (
                                  <span className="skill-tag more-skills">+{resume.job_analysis.key_skills.length - 3}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}
        </motion.div>

        {/* Quick tips section */}
        <motion.div 
          className="dashboard-section"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="section-header">
            <h2>Resume Tips</h2>
          </div>
          
          <div className="tips-grid">
            {[
              {
                icon: <FaSearch />,
                title: "Keyword Optimization",
                text: "Include keywords from the job description to pass through applicant tracking systems."
              },
              {
                icon: <FaChartBar />,
                title: "Quantify Achievements",
                text: "Use numbers and percentages to demonstrate your impact in previous positions."
              },
              {
                icon: <FaEdit />,
                title: "Clear Formatting",
                text: "Keep your resume clean with consistent formatting and easy-to-read fonts."
              },
              {
                icon: <FaBullseye />,
                title: "Tailor Each Resume",
                text: "Customize your resume for each job application to highlight relevant skills."
              }
            ].map((tip, index) => (
              <motion.div 
                key={index} 
                className="tip-card"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)"
                }}
              >
                <motion.div 
                  className="tip-icon"
                  whileHover={{ rotate: 10, scale: 1.1 }}
                >
                  {tip.icon}
                </motion.div>
                <motion.h3 
                  className="tip-title"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {tip.title}
                </motion.h3>
                <p className="tip-text">{tip.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Dashboard;